import React from 'react';
import PropTypes from 'prop-types';
import Loading from 'app/loading';
import { Image } from 'rebass';
import { BiInfoCircle } from 'react-icons/bi';
import { PrimaryButton, OutlineButton, ErrorText, JournalCard, CardTitle, SpecText, SectionPrompt } from '../styled';
import { formatPrice } from './money';
import {
  StatusWrap,
  StatusHeading,
  StatusCopy,
  StatusActions,
  OptionList,
  OptionRow,
  OptionNameButton,
  OptionNameText,
  OptionRight,
  OptionPrice,
} from './styled';
import stork from '../assets/Stork.png';
import { colors } from 'account-dashboard/tokens';

// Same groups PurchaseRow treats as having a marketing detail view — a row
// here only opens one if its underlying option is one of those.
const HAS_DETAIL = group => group === 'add_on' || group === 'feature' || group === 'upgrade';

// The design's one "Processing your order" frame covers everything between
// tapping Purchase and the purchase landing — starting the draft order, handing
// off to Shopify, and polling for payment are all the same visual, just with
// different copy (task requirement: distinguish starting checkout from verifying
// payment, which there is no separate mock for).
const StatusScreen = ({
  phase,
  errorMessage,
  invoiceUrl,
  backedOff,
  title,
  spec,
  selectedOptions,
  onCheckNow,
  onReopenInvoice,
  onRetry,
  onDone,
  onShowDetail,
}) => {
  if (phase === 'creating') {
    return (
      <StatusWrap>
        <Loading local />
        <StatusHeading>Starting your purchase…</StatusHeading>
      </StatusWrap>
    );
  }

  // Shopify has the draft order and it's sitting unpaid ("open") — the member
  // may have abandoned checkout or just come back to this tab. Show them
  // exactly what they were buying and let them either pick up where they left
  // off or cancel outright, rather than silently polling behind the scenes.
  // `pending` (the brief instant before Shopify confirms the draft order)
  // still reads as the generic 'waiting' screen below — this is specifically
  // for a draft order Shopify has already acknowledged.
  if (phase === 'open') {
    return (
      <StatusWrap style={{ alignItems: 'stretch', textAlign: 'left', padding: '20px 0' }}>
        <StatusHeading style={{ textAlign: 'left' }}>Continue with your order?</StatusHeading>
        {/* <SectionPrompt>Add-ons you&rsquo;ve selected for this book:</SectionPrompt>

        <JournalCard>
          <CardTitle>{title}</CardTitle>
          {!!spec && <SpecText>{spec}</SpecText>}
        </JournalCard> */}

        <OptionList>
          {selectedOptions.map(option => (
            <OptionRow key={option.id}>
              {(option.group) ? (
                <OptionNameButton type="button" onClick={() => onShowDetail(option)}>
                  {option.title}
                  <BiInfoCircle size={14} />
                </OptionNameButton>
              ) : (
                <OptionNameText style={{ color: colors.brand }}> {option.title} </OptionNameText>
              )}
              <OptionRight>
                <OptionPrice>
                  {formatPrice(option.priceCents, option.currency)}
                  {option.quantity > 1 ? ` × ${option.quantity}` : ''}
                </OptionPrice>
              </OptionRight>
            </OptionRow>
          ))}
        </OptionList>
      </StatusWrap>
    );
  }

  if (phase === 'waiting') {
    return (
      <StatusWrap>
        <Image width="217.001px" src={stork} alt="Processing your order" />
        <StatusHeading>Processing your order</StatusHeading>
        <Loading local />
        <StatusCopy>Please hold on while we apply your add-ons to your book</StatusCopy>
        {backedOff && (
          <StatusActions>
            <StatusCopy>This is taking longer than usual.</StatusCopy>
            <PrimaryButton type="button" inline onClick={onCheckNow}>
              Check again
            </PrimaryButton>
            <OutlineButton type="button" inline onClick={onReopenInvoice}>
              Reopen checkout
            </OutlineButton>
          </StatusActions>
        )}
      </StatusWrap>
    );
  }

  if (phase === 'completed') {
    return (
      <StatusWrap>
        <StatusHeading>You&rsquo;re all set!</StatusHeading>
        <StatusCopy>Your purchase has been added to this book.</StatusCopy>
        <StatusActions>
          <PrimaryButton type="button" onClick={onDone}>
            Done
          </PrimaryButton>
        </StatusActions>
      </StatusWrap>
    );
  }

  if (phase === 'failed') {
    return (
      <StatusWrap>
        <StatusHeading>We couldn&rsquo;t complete that purchase.</StatusHeading>
        {!!errorMessage && <ErrorText>{errorMessage}</ErrorText>}
        <StatusActions>
          <PrimaryButton type="button" onClick={onRetry}>
            Try again
          </PrimaryButton>
        </StatusActions>
      </StatusWrap>
    );
  }

  if (phase === 'unresolved') {
    // Deliberately generic — the API guide is explicit that `error-message`
    // names an internal situation and must not reach the member.
    return (
      <StatusWrap>
        <StatusHeading>We&rsquo;re sorting this out.</StatusHeading>
        <StatusCopy>Our team has already been notified. Please check back shortly.</StatusCopy>
        <StatusActions>
          <PrimaryButton type="button" onClick={onDone}>
            Back to My Account
          </PrimaryButton>
        </StatusActions>
      </StatusWrap>
    );
  }

  return null;
};

StatusScreen.propTypes = {
  phase: PropTypes.oneOf(['creating', 'open', 'waiting', 'completed', 'failed', 'unresolved']).isRequired,
  errorMessage: PropTypes.string,
  invoiceUrl: PropTypes.string,
  backedOff: PropTypes.bool,
  title: PropTypes.string,
  spec: PropTypes.string,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      group: PropTypes.string,
      priceCents: PropTypes.number,
      currency: PropTypes.string,
      quantity: PropTypes.number,
    })
  ),
  onCheckNow: PropTypes.func,
  onReopenInvoice: PropTypes.func,
  onRetry: PropTypes.func,
  onDone: PropTypes.func,
  onShowDetail: PropTypes.func,
};

StatusScreen.defaultProps = {
  errorMessage: null,
  invoiceUrl: null,
  backedOff: false,
  title: '',
  spec: '',
  selectedOptions: [],
  onCheckNow: () => { },
  onReopenInvoice: () => { },
  onRetry: () => { },
  onDone: () => { },
  onShowDetail: () => { },
};

export default StatusScreen;
